import { useEffect, useRef, useState } from "react";
import { signatureImageFromFile } from "./gespeicherte-unterschrift";
import { SIGNATURE_INK } from "./tinte";
import "./vertraege.css";
import { currentLang } from "../../../lib/sprache";

/** Texte des Unterschriftsfelds in der Sprache der Oberfläche des Unterzeichners (Befund 8). */
export const PAD_TEXT = {
  de: { name: "Vollständiger Name", kind: "Art der Unterschrift", type: "Name tippen", draw: "Unterschrift zeichnen", placeholder: "Dein Name erscheint hier",
    placeholderSie: "Ihr Name erscheint hier", canvas: "Zeichenfläche für die Unterschrift", captured: "Unterschrift erfasst.", hint: "Mit Maus, Finger oder Stift unterschreiben.", redo: "Neu zeichnen",
    image: "Bild verwenden", imageChoose: "Eingescannte Unterschrift wählen (PNG oder JPG, bis 5 MB)", imageAlt: "Deine Unterschrift", imageAltSie: "Ihre Unterschrift", imageHint: "Das Bild wird verkleinert und als PNG übernommen." },
  en: { name: "Full name", kind: "Type of signature", type: "Type name", draw: "Draw signature", placeholder: "Your name appears here",
    placeholderSie: "Your name appears here", canvas: "Drawing area for the signature", captured: "Signature captured.", hint: "Sign with mouse, finger or stylus.", redo: "Draw again",
    image: "Use image", imageChoose: "Choose a scanned signature (PNG or JPG, up to 5 MB)", imageAlt: "Your signature", imageAltSie: "Your signature", imageHint: "The image is reduced in size and used as PNG." },
} as const;

/** Farbe der gezeichneten Unterschrift; die Zeichenfläche ist immer weiß (SICHT-OEFFENTLICH-BETRIEB-15, tinte.ts). */
export { SIGNATURE_INK };

export interface SignatureValue {
  name: string;
  /** gezeichnete Unterschrift als PNG (data-URL) oder null bei getippter Unterschrift */
  dataUrl: string | null;
}

/**
 * Unterschrift: zeichnen (Maus, Finger, Stift) oder den Namen tippen. Die Namenseingabe ist die barrierefreie
 * Alternative zum Zeichnen (Design-Paket). Das Bild wird verkleinert und als PNG übergeben (höchstens 200 KB).
 */
export function SignaturePad({ value, onChange, nameLabel, disabled, nameReadOnly, lang = currentLang(), formal = false, upload = false }: {
  value: SignatureValue;
  onChange: (v: SignatureValue) => void;
  nameLabel?: string;
  disabled?: boolean;
  /** Name steht fest (z. B. aus dem Profil der Agentur-Person) */
  nameReadOnly?: boolean;
  /** Sprache der Oberfläche */
  lang?: "de" | "en";
  /** Sie-Form (Marke, gesetzliche Vertretung) */
  formal?: boolean;
  /**
   * Dritte Art „Bild verwenden“ (PARITAET-12): eingescannte oder gespeicherte Unterschrift. Mit einem vorbelegten Bild
   * (gespeicherte Unterschrift) beginnt das Feld in dieser Art.
   */
  upload?: boolean;
}) {
  const t = PAD_TEXT[lang];
  const [mode, setMode] = useState<"type" | "draw" | "image">(upload && value.dataUrl ? "image" : "type");
  const [imageError, setImageError] = useState<string | null>(null);
  // Bild von außen vorbelegt (gespeicherte Unterschrift): Art „Bild“ zeigen, damit Anzeige und Unterschrift übereinstimmen
  useEffect(() => {
    if (upload && mode === "type" && value.dataUrl) setMode("image");
  }, [upload, mode, value.dataUrl]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (mode !== "draw") return;
    const c = canvas.current;
    if (!c) return;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(c.clientWidth * ratio);
    c.height = Math.round(c.clientHeight * ratio);
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Tinte fest dunkel auf fester weißer Fläche (vertraege.css), unabhängig vom Thema
    ctx.strokeStyle = SIGNATURE_INK;
    dirty.current = false;
  }, [mode]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const exportImage = () => {
    const c = canvas.current;
    if (!c || !dirty.current) return null;
    // auf höchstens 600 Pixel Breite verkleinern, weißer Hintergrund bleibt transparent
    const scale = Math.min(1, 600 / c.width);
    const out = document.createElement("canvas");
    out.width = Math.round(c.width * scale);
    out.height = Math.round(c.height * scale);
    out.getContext("2d")?.drawImage(c, 0, 0, out.width, out.height);
    const url = out.toDataURL("image/png");
    return url.length <= 200_000 ? url : null;
  };
  const clear = () => {
    const c = canvas.current;
    c?.getContext("2d")?.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange({ ...value, dataUrl: null });
  };

  return (
    <div className="tk-sigpad">
      <label>
        {nameLabel ?? t.name}
        <input type="text" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} autoComplete="name" required disabled={disabled} readOnly={nameReadOnly} />
      </label>
      <div className="toolbar" role="group" aria-label={t.kind}>
        <button type="button" aria-pressed={mode === "type"} onClick={() => { setMode("type"); onChange({ ...value, dataUrl: null }); }} disabled={disabled}>{t.type}</button>
        <button type="button" aria-pressed={mode === "draw"} onClick={() => { if (mode !== "draw") { setMode("draw"); onChange({ ...value, dataUrl: null }); } }} disabled={disabled}>{t.draw}</button>
        {upload && <button type="button" aria-pressed={mode === "image"} onClick={() => setMode("image")} disabled={disabled}>{t.image}</button>}
      </div>
      {mode === "image" ? (
        <div className="stack tk-gap-sm">
          {value.dataUrl && <img className="tk-sigimage" src={value.dataUrl} alt={formal ? t.imageAltSie : t.imageAlt} />}
          <label>
            {t.imageChoose}
            <input type="file" accept="image/png,image/jpeg" disabled={disabled} onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              setImageError(null);
              void signatureImageFromFile(file, lang).then((dataUrl) => onChange({ ...value, dataUrl }), (err: unknown) => setImageError(err instanceof Error ? err.message : String(err)));
            }} />
          </label>
          {imageError ? <small className="tk-error" role="alert">{imageError}</small> : <small>{value.dataUrl ? t.captured : t.imageHint}</small>}
        </div>
      ) : mode === "type" ? (
        <div className="signature" aria-live="polite">{value.name.trim() || (formal ? t.placeholderSie : t.placeholder)}</div>
      ) : (
        <>
          <canvas
            ref={canvas}
            aria-label={t.canvas}
            onPointerDown={(e) => { if (disabled) return; drawing.current = true; last.current = pos(e); e.currentTarget.setPointerCapture(e.pointerId); }}
            onPointerMove={(e) => {
              if (!drawing.current) return;
              const ctx = e.currentTarget.getContext("2d");
              const p = pos(e);
              if (ctx && last.current) {
                ctx.beginPath();
                ctx.moveTo(last.current.x, last.current.y);
                ctx.lineTo(p.x, p.y);
                ctx.stroke();
                dirty.current = true;
              }
              last.current = p;
            }}
            onPointerUp={() => { drawing.current = false; last.current = null; onChange({ ...value, dataUrl: exportImage() }); }}
            onPointerLeave={() => { if (drawing.current) { drawing.current = false; onChange({ ...value, dataUrl: exportImage() }); } }}
          />
          <div className="row between">
            <small>{value.dataUrl ? t.captured : t.hint}</small>
            <button type="button" onClick={clear} disabled={disabled}>{t.redo}</button>
          </div>
        </>
      )}
    </div>
  );
}
